import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getMerchant, getMerchantProducts } from '../api/merchants'
import { listCategories } from '../api/categories'
import Spinner from '../components/common/Spinner'
import type { CrowdMerchant, CrowdProduct, Category } from '../types'

const inputStyle = {
  background: 'var(--app-card-bg)',
  color: 'var(--tg-theme-text-color)',
  border: '1px solid transparent',
  borderRadius: '12px',
  padding: '9px 12px 9px 36px',
  width: '100%',
  fontSize: '14px',
  outline: 'none',
}

// ─── Product Preview Sheet ────────────────────────────────────────────────────

function ProductSheet({
  product,
  shopName,
  merchantId,
  onClose,
  onAdd,
}: {
  product: CrowdProduct
  shopName: string
  merchantId: string
  onClose: () => void
  onAdd: () => void
}) {
  // Close on backdrop click
  return (
    <div
      className="fixed inset-0 z-50 flex items-end"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-3xl overflow-hidden"
        style={{ background: 'var(--tg-theme-bg-color)', maxHeight: '85vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full opacity-30" style={{ background: 'var(--tg-theme-text-color)' }} />
        </div>

        {/* Image */}
        <div className="w-full aspect-square" style={{ background: 'var(--app-page-bg)' }}>
          {(product.medium_url || product.thumbnail_url) ? (
            <img src={product.medium_url || product.thumbnail_url} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-5xl opacity-20">📷</div>
          )}
        </div>

        <div className="p-4 space-y-3 pb-8">
          <div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
              {product.name}
            </h2>
            {product.price != null && (
              <p className="text-lg font-bold mt-1" style={{ color: 'var(--tg-theme-link-color)' }}>
                {product.price.toLocaleString()} {product.price_currency ?? 'UZS'}
              </p>
            )}
          </div>

          <p className="text-sm opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
            🏪 {shopName}
          </p>

          {product.description && (
            <p className="text-sm opacity-70 leading-relaxed" style={{ color: 'var(--tg-theme-text-color)' }}>
              {product.description}
            </p>
          )}

          {product.status === 'crowd_proposed' && (
            <p className="text-xs px-2 py-1 rounded-lg inline-block" style={{ background: '#f59e0b18', color: '#f59e0b' }}>
              {tWorker('shopDetail.communitySubmitted')}
            </p>
          )}

          {/* TODO: "Update price" flow — link this to product-specific submission once
               the backend supports updating an existing product via crowd submission.
          <button
            className="w-full py-3 rounded-2xl font-semibold text-base"
            style={{ background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
            onClick={onAdd}
          >
            + Add Updated Price for This Product
          </button>
          */}
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ShopDetailPage() {
  const { t: tWorker } = useTranslation('worker')
  const { merchantId } = useParams<{ merchantId: string }>()
  const navigate = useNavigate()

  const [merchant, setMerchant] = useState<CrowdMerchant | null>(null)
  const [products, setProducts] = useState<CrowdProduct[]>([])
  const [total, setTotal] = useState(0)
  const [allCategories, setAllCategories] = useState<Category[]>([])
  // Category IDs seen on the initial unfiltered load — kept stable so chips don't disappear when filtering
  const seenCategoryIdsRef = useRef<Set<string>>(new Set())
  const pageRef = useRef(0)
  const pageSize = 20

  const [loading, setLoading] = useState(true)
  const [productLoading, setProductLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [localSearch, setLocalSearch] = useState('')
  const [search, setSearch] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const [previewProduct, setPreviewProduct] = useState<CrowdProduct | null>(null)

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearch(localSearch.trim()), 400)
    return () => clearTimeout(t)
  }, [localSearch])

  // Load merchant + categories in parallel
  useEffect(() => {
    if (!merchantId) return
    setLoading(true)
    Promise.all([getMerchant(merchantId), listCategories()])
      .then(([m, cats]) => {
        setMerchant(m)
        setAllCategories(cats)
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : tWorker('common:error.failedToLoad', { ns: 'common' })))
      .finally(() => setLoading(false))
  }, [merchantId])

  // Load products whenever merchant/search/category changes
  useEffect(() => {
    if (!merchantId || !merchant) return
    setProductLoading(true)
    pageRef.current = 0
    getMerchantProducts(merchantId, 0, pageSize, search, selectedCategoryId ?? undefined)
      .then(({ items, total: t }) => {
        setProducts(items)
        setTotal(t)
        // Only capture category IDs when no filters are active so the chip list stays stable
        if (!search && !selectedCategoryId) {
          items.forEach((p) => { if (p.category_id) seenCategoryIdsRef.current.add(p.category_id) })
        }
      })
      .catch(() => {})
      .finally(() => setProductLoading(false))
  }, [merchantId, merchant, search, selectedCategoryId])

  const loadMore = () => {
    if (!merchantId || productLoading) return
    const nextPage = pageRef.current + 1
    setProductLoading(true)
    getMerchantProducts(merchantId, nextPage * pageSize, pageSize, search, selectedCategoryId ?? undefined)
      .then(({ items }) => {
        setProducts((prev) => [...prev, ...items])
        pageRef.current = nextPage
      })
      .catch(() => {})
      .finally(() => setProductLoading(false))
  }

  // Derive category chips from the stable seen-set + allCategories lookup
  const categoryMap = new Map(allCategories.map((c) => [c.id, c]))
  const visibleCategories = [...seenCategoryIdsRef.current]
    .map((id) => categoryMap.get(id))
    .filter(Boolean) as Category[]

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-page-bg)' }}>
        <Spinner />
      </div>
    )
  }

  if (error || !merchant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: 'var(--app-page-bg)' }}>
        <p style={{ color: '#ef4444' }}>{error ?? tWorker('taskItemDetail.itemNotFound')}</p>
        <button className="mt-4 text-sm" style={{ color: 'var(--tg-theme-link-color)' }} onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
    )
  }

  const handleAddProduct = () => {
    navigate(`/free-submit?merchant_id=${merchantId}&shop_name=${encodeURIComponent(merchant.business_name)}`)
  }

  const hasMore = products.length < total

  return (
    <div className="min-h-screen pb-28 safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>

      {/* White header section */}
      <div style={{ background: 'var(--tg-theme-bg-color)' }}>
        <div className="p-4 pb-0">
          <button className="text-sm opacity-60" style={{ color: 'var(--tg-theme-link-color)' }} onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>

        <div className="px-4 pt-3 pb-4">
          {/* Identity row */}
          <div className="flex items-start gap-3">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
              style={{ background: 'var(--app-card-bg)' }}
            >
              🏪
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h1 className="text-xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
                  {merchant.business_name}
                </h1>
                {merchant.is_crowd_verified && (
                  <span className="text-sm" style={{ color: '#22c55e' }}>✓</span>
                )}
              </div>
              {merchant.shop_category && (
                <p className="text-sm opacity-50 mt-0.5" style={{ color: 'var(--tg-theme-text-color)' }}>
                  {merchant.shop_category}
                </p>
              )}
            </div>
          </div>

          {/* Meta */}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
            {merchant.address && <span>📍 {merchant.address}</span>}
            {merchant.contact_phone && <span>📞 {merchant.contact_phone}</span>}
          </div>
          {merchant.description && (
            <p className="text-sm mt-2 opacity-70 leading-relaxed" style={{ color: 'var(--tg-theme-text-color)' }}>
              {merchant.description}
            </p>
          )}
          <p className="text-xs mt-2 opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
            {tWorker('shops.productsCount_many', { count: total })}
          </p>

          {/* Search */}
          <div className="relative mt-3">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
              🔍
            </span>
            <input
              style={inputStyle}
              placeholder={tWorker('shops.searchPlaceholder')}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
            {localSearch && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm opacity-40"
                style={{ color: 'var(--tg-theme-text-color)' }}
                onClick={() => setLocalSearch('')}
              >
                ×
              </button>
            )}
          </div>

          {/* Category chips */}
          {visibleCategories.length > 0 && (
            <div className="mt-3 overflow-x-auto -mx-4 px-4">
              <div className="flex gap-2 pb-1" style={{ width: 'max-content' }}>
                <button
                  className="px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap"
                  style={{
                    background: selectedCategoryId === null ? 'var(--tg-theme-link-color)' : 'var(--app-card-bg)',
                    color: selectedCategoryId === null ? '#ffffff' : 'var(--tg-theme-text-color)',
                  }}
                  onClick={() => setSelectedCategoryId(null)}
                >
                  {tWorker('common:all', { ns: 'common' })}
                </button>
                {visibleCategories.map((cat) => (
                  <button
                    key={cat.id}
                    className="px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap"
                    style={{
                      background: selectedCategoryId === cat.id ? 'var(--tg-theme-link-color)' : 'var(--app-card-bg)',
                      color: selectedCategoryId === cat.id ? '#ffffff' : 'var(--tg-theme-text-color)',
                    }}
                    onClick={() => setSelectedCategoryId(selectedCategoryId === cat.id ? null : cat.id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Products grid */}
      <div className="px-4 pt-4">
        {productLoading && products.length === 0 && (
          <div className="flex justify-center py-12"><Spinner /></div>
        )}

        {!productLoading && products.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">📦</div>
            <p className="text-sm opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
              {search || selectedCategoryId ? tWorker('shopDetail.noProductsFiltered') : tWorker('shopDetail.noProducts')}
            </p>
            {!search && !selectedCategoryId && (
              <button className="mt-3 text-sm font-medium" style={{ color: 'var(--tg-theme-link-color)' }} onClick={handleAddProduct}>
                {tWorker('shopDetail.addFirstProduct')}
              </button>
            )}
          </div>
        )}

        {products.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="rounded-2xl overflow-hidden cursor-pointer active:scale-95 transition-transform"
                  style={{ background: 'var(--app-card-bg)' }}
                  onClick={() => setPreviewProduct(product)}
                >
                  <div className="relative aspect-square" style={{ background: 'var(--app-page-bg)' }}>
                    {product.thumbnail_url ? (
                      <img src={product.thumbnail_url} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl opacity-20">📷</div>
                    )}
                    {product.status === 'crowd_proposed' && (
                      <span
                        className="absolute top-1.5 right-1.5 text-xs px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: 'rgba(245,158,11,0.85)', color: '#fff' }}
                      >
                        new
                      </span>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-sm font-medium line-clamp-2 leading-snug" style={{ color: 'var(--tg-theme-text-color)' }}>
                      {product.name}
                    </p>
                    {product.price != null && (
                      <p className="text-sm font-bold mt-1" style={{ color: 'var(--tg-theme-link-color)' }}>
                        {product.price.toLocaleString()} {product.price_currency ?? 'UZS'}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  className="px-6 py-2.5 rounded-xl text-sm font-medium disabled:opacity-40"
                  style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
                  onClick={loadMore}
                  disabled={productLoading}
                >
                  {productLoading ? tWorker('common:loading_', { ns: 'common' }) : tWorker('shopDetail.loadMore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky Add Product button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
        <button
          className="w-full py-3 rounded-2xl font-semibold text-base"
          style={{ background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
          onClick={handleAddProduct}
        >
          {tWorker('shopDetail.addProduct')}
        </button>
      </div>

      {/* Product preview sheet */}
      {previewProduct && (
        <ProductSheet
          product={previewProduct}
          shopName={merchant.business_name}
          merchantId={merchantId!}
          onClose={() => setPreviewProduct(null)}
          onAdd={() => {
            setPreviewProduct(null)
            handleAddProduct()
          }}
        />
      )}
    </div>
  )
}
